import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import JSOnlyLogo from '../images/JSOnly.svg';
import SuccessImage from '../images/success.svg';
import bgImage from '../assets/background.jpg';
import { Oval } from 'react-loader-spinner';

const Upload = () => {
  const { beratungsID } = useParams();
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [signatureLink, setSignatureLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedAgb, setAcceptedAgb] = useState(false);
  const [showAgbModal, setShowAgbModal] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Fetch the initial signature link when the component mounts
    fetch(`https://jurasolutions.pro/api/getSignatureLink/${beratungsID}`)
      .then(response => response.json())
      .then(data => setSignatureLink(data.signatureLink))
      .catch(error => console.error('Error fetching signature link:', error));
  }, [beratungsID]);

  const handleFileChange = (event) => {
    setFiles(event.target.files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      setFiles(droppedFiles);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
  };

  const handleUpload = () => {
    if (files.length === 0) {
      alert('Bitte wählen Sie zuerst eine Datei aus!');
      return;
    }

    setLoading(true);

    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('files', file));

    fetch(`https://jurasolutions.pro/api/upload/${beratungsID}`, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setLoading(false);
        if (data.success) {
          setUploadSuccess(true);
          setSignatureLink(data.signatureLink);
        } else {
          alert('Fehler beim Hochladen der Datei.');
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error('Fehler beim Hochladen der Datei:', error);
      });
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleAddMoreDocuments = () => {
    setUploadSuccess(false);
    setFiles([]);
  };

  const handleSkipUpload = () => {
    if (!acceptedAgb) return;
    if (signatureLink) {
      window.location.href = signatureLink;
    } else {
      alert('Kein Signatur-Link verfügbar.');
    }
  };

  const pageStyle = {
    backgroundImage: `url(${bgImage})`,
    backgroundAttachment: 'fixed',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-center" style={pageStyle}>
      {uploadSuccess ? (
        <div className="max-w-md w-full mx-4">
          <div className="flex justify-center mb-8">
            <img src={JSOnlyLogo} alt="JuraSolutions" className="w-56 h-auto" />
          </div>
          <div className="glass-apple p-8 rounded-xl text-center">
            <img src={SuccessImage} alt="Success" className="mb-6 mx-auto w-24" />
            <h2 className="assistant-heading text-black/80 mb-2">Upload erfolgreich!</h2>
            <p className="text-black/60 mb-6">Ihre Dateien wurden erfolgreich hochgeladen.</p>
            <div className="space-y-3">
              <a
                href={signatureLink}
                className="block w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center"
              >
                Zur Signatur
              </a>
              <button
                onClick={handleAddMoreDocuments}
                className="w-full bg-white/50 text-black py-3 rounded-lg font-semibold border border-black/20 hover:bg-white/70 transition-colors"
              >
                Weitere Dokumente hochladen
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-md w-full mx-4">
         
          <div className="glass-apple p-8 rounded-xl">
            <h1 className="assistant-heading text-black/80 mb-2">Nächster Schritt</h1>
            <p className="subheader text-black/60 mb-6">Fall: {beratungsID}</p>
            <p className="text-black/70 mb-6 leading-relaxed">
              Sie haben zwei Möglichkeiten: <strong>Zusätzliche Dokumente hochladen</strong> oder <strong>direkt zur Signatur</strong> gehen. 
            </p>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={handleClick}
              className={`relative border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors ${
                dragOver ? 'border-black/40 bg-white/40' : 'border-black/20 bg-white/30 hover:bg-white/40'
              }`}
            >
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                ref={fileInputRef}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <p className="text-center text-black/70">
                {files.length > 0 ? (
                  <span className="font-medium">{Array.from(files).map(f => f.name).join(', ')}</span>
                ) : (
                  <>Datei hierher ziehen oder klicken, um auszuwählen</>
                )}
              </p>
            </div>
            <div className="mt-6 flex items-start gap-3">
              <input
                id="agb"
                type="checkbox"
                checked={acceptedAgb}
                onChange={(e) => setAcceptedAgb(e.target.checked)}
                className="mt-1 rounded border-black/30"
              />
              <label htmlFor="agb" className="text-sm text-black/70">
                Ich habe die{' '}
                <button
                  type="button"
                  onClick={() => setShowAgbModal(true)}
                  className="text-black/80 underline hover:text-black font-medium"
                >
                  AGB
                </button>{' '}
                gelesen und akzeptiere diese.
              </label>
            </div>
            {!loading && (
              <div className="flex flex-col sm:flex-row mt-6 gap-3">
                <button
                  onClick={handleSkipUpload}
                  disabled={!acceptedAgb}
                  className="flex-1 w-full bg-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Zur Signatur
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!acceptedAgb}
                  className="flex-1 w-full bg-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Dokumente hochladen
                </button>
              </div>
            )}
            {loading && (
              <div className="flex justify-center mt-6">
                <Oval
                  height={40}
                  width={40}
                  color="#1a1a2e"
                  secondaryColor="#1a1a2e"
                  strokeWidth={2}
                  strokeWidthSecondary={2}
                />
              </div>
            )}
          {showAgbModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="glass-apple w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-black/10">
                  <h3 className="text-lg font-semibold text-black/80">Allgemeine Geschäftsbedingungen (AGB)</h3>
                  <button
                    type="button"
                    onClick={() => setShowAgbModal(false)}
                    className="text-black/60 hover:text-black p-1"
                    aria-label="Modal schließen"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4 overflow-y-auto flex-1">
                  <div className="space-y-4 text-sm leading-6 text-black/80">
                    <p className="italic">der JuraSolutions Rechtsanwaltsgesellschaft mbH</p>

                    <section>
                      <h4 className="font-semibold mb-1">§ 1 Vertragsgegenstand, Vertragsschluss</h4>
                      <p>
                        Mit Absenden des Eingabeformulars und der Übersendung der notwendigen Vollmachten übersendet der
                        Einsendende ein verbindliches Angebot für einen entgeltlichen Rechtsanwaltsvertrag
                        (Geschäftsbesorgungsvertrag in Gestalt eines Dienstvertrages gem. § 675 BGB). Dieser kommt mit einer
                        ausdrücklichen Erklärung der JuraSolutions Rechtsanwaltsgesellschaft mbH an den Einsendenden (im
                        Folgenden: Mandant) zustande (Übersendung einer Bestätigung der Aktenanlage, sowie des Aktenzeichens
                        per E-Mail).
                      </p>
                    </section>

                    <section>
                      <h4 className="font-semibold mb-1">§ 2 Leistungen und Obliegenheiten der JuraSolutions Rechtsanwaltsgesellschaft mbH</h4>
                      <p>
                        Die JuraSolutions Rechtsanwaltsgesellschaft mbH übernimmt das Mandat. Nach Absprache mit dem Mandanten
                        wird dieses außergerichtlich und/oder gerichtlich geführt.
                      </p>
                      <p>
                        Für die Durchsetzung der Ansprüche des Mandanten werden die notwendigen Informationen von der Gegenseite
                        eingeholt.
                      </p>
                      <p>
                        Für einen positiven Ausgang des Verfahrens übernimmt die JuraSolutions Rechtsanwaltsgesellschaft mbH
                        keine Gewähr.
                      </p>
                    </section>

                    <section>
                      <h4 className="font-semibold mb-1">§ 3 Leistungen und Obliegenheiten des Mandanten</h4>
                      <p>
                        Der Mandant bevollmächtigt die JuraSolutions Rechtsanwaltsgesellschaft mbH, seinen Anspruch in seinem
                        Namen durchzusetzen.
                      </p>
                      <p>
                        Er hat der JuraSolutions Rechtsanwaltsgesellschaft mbH alle notwendigen Informationen zur Verfügung zu
                        stellen. Er hat den tatsächlichen Sachverhalt mitzuteilen und alle ihm verfügbaren Dokumente und
                        Nachweise einzureichen. Werden ihm im Laufe des Verfahrens weitere Informationen bekannt, sind diese der
                        JuraSolutions Rechtsanwaltsgesellschaft mbH unverzüglich mitzuteilen.
                      </p>
                      <p>
                        Der Mandant ist verpflichtet, der JuraSolutions Rechtsanwaltsgesellschaft mbH unverzüglich mitzuteilen,
                        wenn er Leistungen der Gegenseite oder einer anderen Stelle erhält, die seine Forderung (teilweise)
                        begleichen sollen. Er ist ferner verpflichtet, die JuraSolutions Rechtsanwaltsgesellschaft mbH über jede
                        Kontaktaufnahme durch die Gegenseite zu unterrichten.
                      </p>
                      <p>
                        Der Mandant bestätigt, dass er zur Durchsetzung der Forderung nur die JuraSolutions
                        Rechtsanwaltsgesellschaft mbH bevollmächtigt hat.
                      </p>
                    </section>

                    <section>
                      <h4 className="font-semibold mb-1">§ 4 Vergütung, Abrechnung, Auszahlung</h4>
                      <p>
                        Der Mandant trägt die für das Verfahren entstandenen Kosten der JuraSolutions
                        Rechtsanwaltsgesellschaft mbH nach dem Rechtsanwaltsvergütungsgesetz. Die Jurasolutions
                        Rechtsanwalts GmbH ist berechtigt im Rahmen der gesetzlichen Vorschriften, Vorschüsse auf Honorare und
                        Auslagen zu erlangen   Nach der Beendigung des Verfahrens, erhält der Mandant Endabrechnung.
                        Überzahlungen oder vereinnahmte Kostenerstattungen der Gegensete werden an den Mandaten ausgekehrt,
                        sofern dieser die Zahlungen tatsächlich geleistet hat.
                      </p>
                      <p>
                        Der Mandant ist im Fall eines von ihm gewünschten Gerichtsprozesses verpflichtet, die durch das Gericht
                        geforderten Vorschüsse zu zahlen.
                      </p>
                    </section>

                    <section>
                      <h4 className="font-semibold mb-1">§ 5 Haftung</h4>
                      <p>
                        Die Haftung der JuraSolutions Rechtsanwaltsgesellschaft mbH ist nach § 52 Abs. 1 Nr. 2 BRAO beschränkt.
                      </p>
                    </section>

                    <section>
                      <h4 className="font-semibold mb-1">§ 6 Dauer des Vertrags, Kündigung</h4>
                      <p>
                        Der Rechtsanwaltsvertrag endet, wenn der Anspruch des Mandanten nach seinen Wünschen durchgesetzt wurde
                        oder das Verfahren rechtskräftig beendet wurde. Er endet ferner, wenn die JuraSolutions
                        Rechtsanwaltsgesellschaft mbH nach ausführlicher Prüfung der Rechtslage festgestellt hat, dass die
                        (weitere) Durchsetzung des Anspruchs aussichtslos ist und sie den Mandanten darüber ausführlich
                        informiert hat.
                      </p>
                      <p>
                        Darüber hinaus kann das Vertragsverhältnis sowohl durch den Mandanten als auch die JuraSolutions
                        Rechtsanwaltsgesellschaft mbH jederzeit gekündigt werden. Der Mandant hat im Falle der Kündigung die bis
                        dahin angefallenen Kosten der JuraSolutions Rechtsanwaltsgesellschaft mbH zu tragen.
                      </p>
                    </section>

                    <section>
                      <h4 className="font-semibold mb-1">§ 7 Streitbeilegung, Schlussbestimmungen</h4>
                      <p>
                        In Fall einer vermögensrechtlichen Streitigkeit aus dem Mandatsverhältnis erklärt sich die JuraSolutions
                        Rechtsanwaltsgesellschaft mbH nicht bereit, an einem Streitbeilegungsverfahren vor einer
                        Verbraucherschlichtungsstelle teilzunehmen.
                      </p>
                      <p>
                        Das Recht der Bundesrepublik Deutschland findet unter Ausschluss des UN-Kaufrechts Anwendung. Ist der
                        Mandant Verbraucher und hat er zum Zeitpunkt der Bestellung der Dienstleistung seinen gewöhnlichen
                        Aufenthalt in einem anderen Land, sind zusätzlich die zwingenden Rechtsvorschriften dieses Landes
                        anwendbar.
                      </p>
                      <p>
                        Ist der Mandant Kaufmann und hat seinen Sitz zum Zeitpunkt der Bestellung der Dienstleistung in
                        Deutschland, ist der ausschließliche Gerichtsstand Düsseldorf. Im Übrigen gelten die gesetzlichen
                        Bestimmungen über die örtliche und internationale Zuständigkeit.
                      </p>
                    </section>

                    <section>
                      <h4 className="font-semibold mb-1">§ 8 Allgemeine Hinweise</h4>
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium">Berufshaftpflichtversicherung</p>
                          <p>
                            R+V Allgemeine Versicherung AG<br />
                            Raiffeisenplatz 1<br />
                            65189 Wiesbaden<br />
                            www.ruv.de<br />
                            Tel: 0800 533- 1116
                          </p>
                        </div>
                        <div>
                          <p className="font-medium">Zuständige Rechtsanwaltskammer</p>
                          <p>
                            Rechtsanwaltskammer Frankfurt am Main<br />
                            Bockenheimer Anlage 36<br />
                            60322 Frankfurt am Main
                          </p>
                          <p>
                            Telefon: +49 69 170098-01<br />
                            Telefax: +49 69 170098-51<br />
                            E-Mail: info@rak-ffm.de
                          </p>
                        </div>
                        <div>
                          <p className="font-medium">Maßgebliche berufsrechtliche Regelungen</p>
                          <p>Für unsere Tätigkeit gelten die nachfolgend genannten berufsrechtlichen Regelungen:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Bundesrechtsanwaltsordnung (BRAO)</li>
                            <li>Berufsordnung für Rechtsanwälte (BORA)</li>
                            <li>Fachanwaltsordnung (FAO)</li>
                            <li>Rechtsanwaltsvergütungsgesetze (RVG)</li>
                            <li>
                              Darüber hinaus gelten für den internationalen Rechtsverkehr die Berufsregeln der Rechtsanwälte der
                              Europäischen Gemeinschaft (CCBE) sowie das Gesetz für die Tätigkeit europäischer Rechtsanwälte in
                              Deutschland (EuRAG).
                            </li>
                          </ul>
                        </div>
                        <p>(Stand: August 2025)</p>
                      </div>
                    </section>
                  </div>
                </div>
                <div className="flex justify-end p-4 border-t border-black/10">
                  <button
                    type="button"
                    onClick={() => setShowAgbModal(false)}
                    className="bg-black text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                  >
                    Schließen
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Upload;